import React, { useEffect, useState } from 'react';
import { AlertTriangle, ShoppingCart, FileText, ArrowLeftRight, Package, Settings } from 'lucide-react';
import PartsManagement from './inventory/PartsManagement';
import PurchaseOrders from './inventory/PurchaseOrders';
import PurchaseRequisition from './inventory/PurchaseRequisition';
import PurchaseReturns from './inventory/PurchaseReturns';
import StockTransfer from './inventory/StockTransfer';
import ServicesManagement from './inventory/ServicesManagement';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const Inventory = () => {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('parts');

  const tabs = [
    { id: 'parts', name: 'Parts Management', icon: Package, permission: 'inventory.parts.view' },
    { id: 'services', name: 'Services Master', icon: Settings, permission: 'inventory.services.view' },
    { id: 'transfer', name: 'Stock Transfer', icon: ArrowLeftRight, permission: 'inventory.stock_transfers.view' },
    { id: 'requisition', name: 'Purchase Requisition', icon: FileText, permission: 'inventory.requisitions.view' },
    { id: 'purchase-orders', name: 'Purchase Orders', icon: ShoppingCart, permission: 'inventory.purchase_orders.view' },
    { id: 'returns', name: 'Purchase Returns', icon: AlertTriangle, permission: 'inventory.purchase_returns.view' }
  ];

  // Filter tabs based on permissions
  const visibleTabs = tabs.filter(tab => hasPermission(tab.permission));

  // Set default tab to first visible tab
  React.useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'parts':
        return <PartsManagement onTabChange={setActiveTab} />;
      case 'services':
        return <ServicesManagement />;
      case 'purchase-orders':
        return <PurchaseOrders />;
      case 'requisition':
        return <PurchaseRequisition />;
      case 'returns':
        return <PurchaseReturns />;
      case 'transfer':
        return <StockTransfer />;
      default:
        return <PartsManagement onTabChange={setActiveTab} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Active Component */}
      <div className="mt-6">
        {renderActiveComponent()}
      </div>
    </div>
  );
};

export default Inventory;